import React, { useEffect, useMemo } from 'react'
import PartiTable from 'components/Shared/MuiTable/PartiTable'
import {
    getIndividualPerformanceColumnsDef,
    getIndividualPerformanceRowsDef,
    IndividualPerformanceRowProps,
} from './IndividualPerformanceTableDef'
import { TableViewType } from 'components/Shared/MuiTable'
import styled from 'particles/themes/styled'
import { CampaignState } from 'state/campaigns'
import { ReportSummaryContainer } from '../../utils'
import { GridPinnedRowsProp } from '@mui/x-data-grid-pro'
import {
    getIndividualPerformance,
    getIndividualPerformanceFilters,
    getIndividualPerformanceStats,
    selectIndividualPerformance,
    selectIndividualPerformanceStats,
    UpdateAmbassadorFavorite,
    UpdateAmbassadorFavoritePayload,
    selectSelectedIndividualPerformanceSortItem,
    selectIndividualPerformanceFilteringParams,
    selectIndividualPerformanceSearchTerm,
} from 'state/campaignReport'
import { useDispatch, useSelector } from 'react-redux'
import { STATE_KEYS } from 'state/requests'
import { selectRequestsAreLoading } from 'state'
import { MAX_PAGE_SIZE, selectActivePageNum } from 'state/review'
import { useNewTranslation, usePrevious } from 'hooks'
import _ from 'lodash'
interface IndividualPerformanceProps {
    campaign: CampaignState
}

const IndividualPerformance: React.FC<IndividualPerformanceProps> = ({ campaign }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const individualPerformance = useSelector(selectIndividualPerformance)
    const individualPerformanceStats = useSelector(selectIndividualPerformanceStats)
    const filteringParams = useSelector(selectIndividualPerformanceFilteringParams)
    const prevFilteringParams = usePrevious(filteringParams)
    const sortQuery = useSelector(selectSelectedIndividualPerformanceSortItem)
    const activePageNum = useSelector(selectActivePageNum)
    const searchTerm = useSelector(selectIndividualPerformanceSearchTerm)

    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.GetIndividualPerformance]))

    const offset: number = activePageNum * MAX_PAGE_SIZE

    const handleClickFavorite = (payload: UpdateAmbassadorFavoritePayload) => {
        const { favorited, ...rest } = payload

        dispatch(
            UpdateAmbassadorFavorite({
                ...rest,
                favorited: !favorited,
            }),
        )
    }

    const individualPerformanceColumns = getIndividualPerformanceColumnsDef(
        campaign,
        handleClickFavorite,
        individualPerformance,
    )
    const individualPerformanceRows = getIndividualPerformanceRowsDef(individualPerformance, individualPerformanceStats)

    const { rows, aggregateRow } = individualPerformanceRows

    const pinnedRows: GridPinnedRowsProp | undefined = useMemo(() => {
        if (rows.length === 0) return undefined
        return {
            bottom: [aggregateRow],
        }
    }, [rows.length, aggregateRow, searchTerm])

    const memoizedFilteringParams = useMemo(() => {
        return {
            ...filteringParams,
            keywords: searchTerm,
        }
    }, [_.isEqual(filteringParams, prevFilteringParams), searchTerm])

    useEffect(() => {
        dispatch(
            getIndividualPerformance({
                uuid: campaign.uuid,
                offset,
                keywords: searchTerm,
                ordering: sortQuery ? sortQuery : undefined,
                filteringParams: memoizedFilteringParams,
            }),
        )
        dispatch(getIndividualPerformanceFilters({ uuid: campaign.uuid }))
        dispatch(getIndividualPerformanceStats({ uuid: campaign.uuid }))
    }, [campaign.uuid, activePageNum, sortQuery, memoizedFilteringParams, searchTerm])

    return (
        <ReportSummaryContainer title={t('submission.campaignReport.individualPerformance')}>
            <TableContainer>
                <PartiTable
                    {...(rows.length > 0 && { tableHeight: 'auto' })}
                    viewType={TableViewType.INDIVIDUAL_PERFORMANCE}
                    headerHeight={56}
                    rowHeight={72}
                    getRowHeight={() => 'auto'}
                    columns={individualPerformanceColumns}
                    rows={rows as IndividualPerformanceRowProps[]}
                    isLoading={isLoading}
                    hideToolbar={false}
                    disableColumnMenu
                    noPadding
                    hideTableBorder
                    isNewStyle
                    {...(rows.length > 0 && {
                        getRowClassName: params => {
                            return pinnedRows?.bottom?.[0]?.id === params?.id ? 'row-total' : ''
                        },
                    })}
                    enableExport={false}
                    showFilter
                    showSort
                    pinnedRows={pinnedRows}
                    showMoreAction
                    tableHeight={`calc(100vh - 300px)`}
                />
            </TableContainer>
        </ReportSummaryContainer>
    )
}

export default IndividualPerformance

const TableContainer = styled.div`
    width: 100%;
    .MuiDataGrid-columnHeaders {
        color: ${({ theme }) => theme.colors.text.primary};
        background: ${({ theme }) => theme.colors.surface.infoPastel};
    }

    .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox) .MuiDataGrid-columnHeaderTitleContainer {
        margin-right: 0;
    }

    .new-style-MUI .MuiDataGrid-columnHeaderTitle {
        color: ${({ theme }) => theme.colors.text.primary};
    }

    .performance-header {
        .MuiDataGrid-columnHeaderTitleContainerContent div {
            flex-direction: column;
        }
    }

    .MuiDataGrid-columnHeaderTitleContainerContent {
        margin: auto;
    }

    .align-left-header {
        .MuiDataGrid-columnHeaderTitleContainerContent {
            margin-left: 2px;
        }
    }

    .MuiDataGrid-pinnedRows--bottom {
        background: ${({ theme }) => theme.colors.surface.tertiary};
        height: auto;
    }

    .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row {
        min-height: 36px !important;
    }

    .row-total .MuiDataGrid-cell {
        > *:last-child {
            font-weight: 700;

            .cellValue {
                font-weight: 700;
            }

            .icon-follower-section div {
                font-weight: 700;
            }
        }
    }
`
